import { useState, useRef, useEffect } from "react";
import uploadIcon from "../assets/upload.svg";
import { exportCgmFile } from "../data/postcgm";

const S3_BASE = process.env.REACT_APP_S3_BASE_URL || "https://neosynkserver.s3.ap-south-1.amazonaws.com";

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const QUICK_RANGES = [
  { label: "Today",       start: () => daysAgo(0), end: () => daysAgo(0) },
  { label: "Last 2 days", start: () => daysAgo(1), end: () => daysAgo(0) },
  { label: "Last 3 days", start: () => daysAgo(2), end: () => daysAgo(0) },
];

// ─── ExportDateModal ──────────────────────────────────────────────────────
function ExportDateModal({ onClose, onConfirm, loading }) {
  const today = toYMD(new Date());
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const ref = useRef(null);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const applyQuickRange = (range) => {
    setStartDate(toYMD(range.start()));
    setEndDate(toYMD(range.end()));
  };

  const isValid = startDate && endDate && startDate <= endDate;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: 320,
          maxWidth: "90vw",
          padding: "20px 22px 22px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Export CGM Data</span>
          <button
            onClick={onClose}
            style={{
              width: 26, height: 26, borderRadius: "50%", border: "none",
              background: "#f1f5f9", color: "#334155", fontSize: 13,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
            Start date
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                display: "block", width: "100%", marginTop: 4,
                padding: "8px 10px", borderRadius: 8, border: "1px solid #e2e8f0",
                fontSize: 13, color: "#0f172a",
              }}
            />
          </label>
          <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
            End date
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={today}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                display: "block", width: "100%", marginTop: 4,
                padding: "8px 10px", borderRadius: 8, border: "1px solid #e2e8f0",
                fontSize: 13, color: "#0f172a",
              }}
            />
          </label>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginBottom: 6 }}>Quick select</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {QUICK_RANGES.map((r) => (
              <button
                key={r.label}
                onClick={() => applyQuickRange(r)}
                style={{
                  padding: "6px 12px", borderRadius: 20, border: "1px solid #e2e8f0",
                  background: "#f8fafc", color: "#334155", fontSize: 12, fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => isValid && onConfirm(startDate, endDate)}
          disabled={!isValid || loading}
          style={{
            width: "100%", padding: "10px 0", borderRadius: 10, border: "none",
            background: !isValid || loading ? "#c7c9f5" : "#6366f1",
            color: "#fff", fontSize: 14, fontWeight: 700,
            cursor: !isValid || loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Exporting…" : "Export"}
        </button>
      </div>
    </div>
  );
}

// ─── ExportCgm ────────────────────────────────────────────────────────────
export default function ExportCgm({ patient }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const openModal = () => {
    if (!patient?.owner_id) {
      alert("Select a patient first.");
      return;
    }
    setModalOpen(true);
  };

  const handleExport = async (startDate, endDate) => {
    setLoading(true);
    console.log("Exporting CGM with params:", {
      patient_owner_id: patient.owner_id,
      start_date: startDate,
      end_date: endDate,
    });
    try {
      const result = await exportCgmFile(patient.owner_id, startDate, endDate);
      const s3Key = result?.s3_key;

      if (!s3Key) {
        alert("Export failed ❌ (no file returned).");
        return;
      }

      const cleanKey = s3Key.replace(/^\/+/, "");
      const fileUrl = `${S3_BASE}/${cleanKey}`;

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = cleanKey.split("/").pop() || "cgm_export.xlsx";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();

      setModalOpen(false);
    } catch (err) {
      console.error("💥 Export failed:", err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.error;

      if (err.response?.status === 404) {
        alert(backendMessage || "No CGM data found for the selected dates.");
      } else {
        alert(backendMessage || "Export failed ❌");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, width: "fit-content", margin: "0 auto" }}>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textAlign: "center", width: "100%" }}>Export CGM</span>
        <img
          src={uploadIcon}
          className="topbar-upload"
          alt="Export CGM"
          onClick={openModal}
          title="Export CGM data (.xlsx)"
          style={{ cursor: "pointer", display: "block", margin: "0 auto" }}
        />
      </div>

      {modalOpen && (
        <ExportDateModal
          onClose={() => (loading ? null : setModalOpen(false))}
          onConfirm={handleExport}
          loading={loading}
        />
      )}
    </>
  );
}